<script setup>
import { computed, inject, onBeforeMount, onBeforeUnmount, ref, watch } from "vue"
import { useHttpErrorsHandler } from "../../Composables/httpErrorsHandler"
import { useLocalStorage } from "../../Composables/useLocalStorage.js"
import { useDataManager } from "../../Composables/useDataManager.js"
import { useCenters } from "../../Composables/useCenters.js"
import { useAuthUserStore } from "../../Store/AuthUserStore.js"
import _ from 'lodash'
import DataTable from "../../Components/DataTable.vue"
import Modal from "../../Components/Modal.vue"

const { httpErrorsHandler } = useHttpErrorsHandler()
const authUserStore = useAuthUserStore()
const authUser = computed(() => authUserStore.authUser)
const brandConfig = inject('brandConfig')

const storageKey = "Shiftech_CenterList"
const { saveToLocalStorage, loadFromLocalStorage } = useLocalStorage(storageKey)

const {
    url,
    data: centers,
    links,
    meta,
    loading,
    query,
    searchLoaded,
    sortingField,
    sortingFieldsOrder,
    changeSorting,
    search,
    loadData,
    showArchived,
    getArchived,
    hideArchived,
} = useDataManager({
    baseUrl: '/api/centers',
    baseSearchUrl: '/api/search/centers',
    defaultSortingField: 'created_at',
    defaultSortingFieldsOrder: {
        id: 'asc',
        name: 'asc',
        created_at: 'asc',
    },
    onError: (error) => httpErrorsHandler(error),
})

const debouncedSearch = _.debounce(search, 250)

const columns = ref([
    { label: '#ID', field: 'id', sortable: true, slot: 'id', class: 'col-1' },
    { label: 'Nom', field: 'name', sortable: true, slot: 'name', class: 'col-5' },
    { label: 'Téléphone', field: 'phone_number', class: 'col-2' },
    { label: 'Utilisateurs', slot: 'users_count', class: 'col-1 text-center' },
    { label: 'Date création', field: 'created_at', sortable: true, slot: 'created_at', class: 'col-1' },
    { label: 'Détails', slot: 'details', class: 'col-1 text-center' },
    { label: 'Actions', slot: 'actions', class: 'col-1 text-center' },
])

onBeforeMount(() => {
    const savedSettings = loadFromLocalStorage()
    if (savedSettings) {
        url.value = savedSettings.url || url.value
        sortingField.value = savedSettings.sortingField || sortingField.value
        Object.assign(sortingFieldsOrder, savedSettings.sortingFieldsOrder || sortingFieldsOrder)
        query.value = savedSettings.query || ''
        searchLoaded.value = savedSettings.searchLoaded || false
        showArchived.value = savedSettings.showArchived || false
    }

    loadData(url.value)
})

watch(
    [url, sortingField, sortingFieldsOrder, query, searchLoaded, showArchived],
    ([newUrl, newSortingField, newSortingOrder, newQuery, newSearchLoaded, newShowArchived]) => {
        saveToLocalStorage({
            url: newUrl,
            sortingField: newSortingField,
            sortingFieldsOrder: newSortingOrder,
            query: newQuery,
            searchLoaded: newSearchLoaded,
            showArchived: newShowArchived,
        })
    },
    { deep: true }
)

onBeforeUnmount(() => {
    document.querySelectorAll('.modal').forEach(modalElement => {
        const modalInstance = bootstrap.Modal.getInstance(modalElement)
        if (modalInstance) {
            modalInstance.hide()
        }
    })
})

const idToArchive = ref(null)

function showModal(modalId, id) {
    idToArchive.value = id
    const modale = bootstrap.Modal.getOrCreateInstance(document.getElementById(modalId))
    modale.show()
}

function hideModal(modalId) {
    const modale = bootstrap.Modal.getOrCreateInstance(document.getElementById(modalId))
    modale.hide()
}

const {
    archiveCenter,
    restoreCenter,
} = useCenters({
    onArchived: () => {
        loadData(url.value)
        hideModal('modalArchive')
    },
    onRestored: () => {
        loadData(url.value)
    },
    onError: (error) => httpErrorsHandler(error),
})
</script>

<template>
    <div class="row">
        <div class="col-lg-12">
            <div class="card">
                <div class="card-header">
                    <div class="row g-3">
                        <div class="col-md-4">
                            <div class="search-box">
                                <input type="search" class="form-control search" placeholder="Rechercher..." v-model="query" @input="debouncedSearch" id="searchCenters">
                                <i class="ri-search-line search-icon"></i>
                            </div>
                        </div>
                        <div class="col-md-auto ms-auto">
                            <button type="button" class="btn btn-dark waves-light me-2" @click="getArchived" v-if="!showArchived">
                                <i class="ri-archive-fill me-1 align-bottom"></i> Archives
                            </button>
                            <button type="button" class="btn btn-success waves-light me-2" @click="hideArchived" v-else>
                                <i class="ri-community-fill me-1 align-bottom"></i> Centres
                            </button>
                            <router-link :to="{ name:'centers.create' }" class="btn btn-secondary" v-if="authUser.can && authUser.can.create_center">
                                <i class="ri-add-fill me-1 align-bottom"></i> Ajouter
                            </router-link>
                        </div>
                    </div>
                </div>
                <div class="card-body">
                    <data-table
                        :columns="columns"
                        :data="centers"
                        :loading="loading"
                        :links="links"
                        :meta="meta"
                        :sorting-field="sortingField"
                        :sorting-order="sortingFieldsOrder"
                        :search-loaded="searchLoaded"
                        @sort-changed="changeSorting"
                        @page-changed="loadData"
                    >
                        <!-- ID -->
                        <template #id="{ item }">
                            <span class="fw-medium text-dark">#{{ item.data.id }}</span>
                        </template>

                        <!-- Nom avec logo -->
                        <template #name="{ item }">
                            <div class="d-flex align-items-center">
                                <div class="flex-shrink-0">
                                    <img :src="brandConfig.logoBlackSmall" alt="" class="avatar-xs">
                                </div>
                                <div class="flex-grow-1 ms-2 name">{{ item.data.name }}</div>
                            </div>
                        </template>

                        <!-- Utilisateurs -->
                        <template #users_count="{ item }">
                            <span class="badge rounded-pill bg-danger-subtle text-danger fs-11" v-if="item.data.users_count === 0">{{ item.data.users_count }}</span>
                            <span class="badge rounded-pill bg-secondary-subtle text-secondary fs-11" v-else>{{ item.data.users_count }}</span>
                        </template>

                        <!-- Date création -->
                        <template #created_at="{ item }">
                            {{ item.data.created_at_fr }}
                        </template>

                        <!-- Détails -->
                        <template #details="{ item }">
                            <router-link :to="{ name: 'centers.show', params: { id: item.data.id } }" class="btn btn-secondary bg-gradient waves-effect waves-light btn-sm">
                                Accéder
                            </router-link>
                        </template>

                        <!-- Actions -->
                        <template #actions="{ item }">
                            <!-- UPDATE -->
                            <router-link :to="{ name: 'centers.edit', params: { id: item.data.id } }" class="text-info" title="Modifier le centre" v-if="item.can.update">
                                <i class="ri-pencil-fill fs-17 px-1"></i>
                            </router-link>
                            <i class="ri-pencil-fill fs-17 px-1 text-muted" v-else></i>

                            <!-- ARCHIVE -->
                            <template v-if="!showArchived">
                                <a href="#" class="text-danger" title="Archiver le centre" @click.prevent="showModal('modalArchive', item.data.id)" v-if="item.can.delete">
                                    <i class="ri-inbox-archive-fill fs-17 px-1"></i>
                                </a>
                                <i class="ri-inbox-archive-fill fs-17 px-1 text-muted" v-else></i>
                            </template>

                            <!-- RESTORE -->
                            <template v-else>
                                <a href="#" class="text-success" title="Restaurer le centre" @click.prevent="restoreCenter(item.data.id)" v-if="item.can.restore">
                                    <i class="ri-inbox-unarchive-fill fs-17 px-1"></i>
                                </a>
                                <i class="ri-inbox-unarchive-fill fs-17 px-1 text-muted" v-else></i>
                            </template>
                        </template>
                    </data-table>

                    <modal id="modalArchive" title="Archiver le centre ?" btn-class="btn-danger" btn-text="Archiver" @click:button="archiveCenter(idToArchive)">
                        <template #icon>
                            <lord-icon src="https://cdn.lordicon.com/gsqxdxog.json" trigger="loop" colors="primary:#212529,secondary:#f06548" style="width:90px;height:90px"></lord-icon>
                        </template>
                        <template #content>
                            <p class="text-muted fs-14 mb-4 pt-1">Vous êtes sur le point d'archiver un centre. Continuer ?</p>
                        </template>
                    </modal>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>

</style>
